/**
 * Minimal DOM HUD: top strip (level name + hint + mission distance + the
 * turn-by-turn guidance line, ADR-022), transient feedback text,
 * end-of-attempt panel, rotate-device overlay. Deliberately NO live scores,
 * NO speed controls, and the bottom band contains only the joystick.
 *
 * Collisions and kerb climbs are WARNED about as they happen — a banner the
 * player cannot miss — and then itemised on the end panel with a time, a
 * place and an impact speed. There is no composite score: a number nobody can
 * trace back to an event is not evidence of anything.
 */

import {
  TUNING_DEFAULTS,
  TUNING_SENSITIVITY_RANGE,
  TUNING_SPEED_RANGE,
} from "../sim/constants";
import type { RoadNetwork } from "../sim/network";
import type { DeliveryPath } from "../sim/path";
import type { RunReport } from "../sim/metrics";
import {
  currentMapId,
  fetchMapIndex,
  mapUrl,
  realMapsEnabled,
  realMapsUrl,
  renderMapList,
  type MapIndexEntry,
} from "./mapPicker";
import { COMFORT_JERK_LAT_MPS3, KERB_HUG_M, TTC_CONFLICT_S } from "../sim/metrics";
import { icon } from "./icons";
import { playTick } from "./sound";
import type { ControlScheme, Guidance, Outcome, Tuning } from "../sim/types";
import type { EnvThemeChoice, RobotModelChoice, SkyChoice, WeatherChoice } from "./tuning";
import { APPEARANCE_THEMES, DEFAULT_APPEARANCE_THEME, type AppearanceThemeId } from "../render/themes";

export interface HudCallbacks {
  onRetry(): void;
  onNext(): void;
  onDownload(): void;
  /** Tweak panel: persist + apply on a fresh attempt. */
  onTuningSave(
    t: Tuning,
    scheme: ControlScheme,
    robot: RobotModelChoice,
    env: EnvThemeChoice,
    appearanceTheme: AppearanceThemeId,
  ): void;
  /** Tweak panel: clear persisted tuning + apply defaults on a fresh attempt. */
  onTuningReset(): void;
  /** Open the map editor (internal tool; hidden during scripted playback). */
  onOpenEditor(): void;
  /** Play the next map in the sequence (end panel). */
  onNextMap(): void;
  /** Load a different random map (welcome card). */
  onRandomMap(): void;
  /** Dismiss the welcome card and start driving. */
  onStart(): void;
  /** Swap between the chase view and the driver's-eye view. */
  onToggleView(): void;
  /** Arm or disarm recording of the driver's view, under this base name. */
  onRecordArm(armed: boolean, name: string): void;
  /** Quick pickers (start card / map panel): apply at once, restart the run. */
  onRobotModel(r: RobotModelChoice): void;
  onEnvTheme(e: EnvThemeChoice): void;
  onMute(on: boolean): void;
  onSky(v: SkyChoice): void;
  onWeather(v: WeatherChoice): void;
  onAppearanceTheme(id: AppearanceThemeId): void;
}

const OUTCOME_TEXT: Record<Outcome["result"], string> = {
  pass_left: "You passed on the left.",
  pass_right: "You passed on the right.",
  yield: "You yielded.",
  completed: "Level complete.",
  collision: "Collision — try again.",
  timeout: "Time ran out — try again.",
  incomplete: "Attempt incomplete.",
};

export class Hud {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly hint: HTMLElement;
  private readonly distance: HTMLElement;
  private readonly minimap: HTMLCanvasElement;
  private readonly minimapCtx: CanvasRenderingContext2D | null;
  /** Static roads + route, drawn once per level. */
  private minimapBase: HTMLCanvasElement | null = null;
  private minimapFit: { ox: number; oz: number; k: number } | null = null;
  private readonly guidance: HTMLElement;
  private readonly guidanceIcon: HTMLElement;
  private readonly guidanceText: HTMLElement;
  private lastGuidanceKey = "";
  private readonly floating: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly panelText: HTMLElement;
  private readonly panelReport: HTMLElement;
  private readonly warningEl: HTMLElement;
  private warningTimer: number | null = null;
  /** Time of the last warning shown, so the same one is not replayed. */
  private lastWarningT = -1;
  private readonly btnNext: HTMLButtonElement;
  private readonly tweakPanel: HTMLElement;
  private readonly tweakSens: HTMLInputElement;
  private readonly tweakSpd: HTMLInputElement;
  private readonly btnEditor: HTMLButtonElement;
  private readonly btnView: HTMLButtonElement;
  private readonly btnRecord: HTMLButtonElement;
  private readonly recordPanel: HTMLElement;
  private readonly recordName: HTMLInputElement;
  private readonly recordNote: HTMLElement;
  private readonly btnRecordArm: HTMLButtonElement;
  /** Whether recording is armed, so the button and the panel agree. */
  private recordArmed = false;
  private readonly endNote: HTMLElement;
  private readonly startPanel: HTMLElement;
  private readonly startMap: HTMLElement;
  private readonly startDesc: HTMLElement;
  private readonly btnNextMap: HTMLButtonElement;
  private readonly btnMaps: HTMLButtonElement;
  private readonly btnEndMaps: HTMLButtonElement;
  private readonly mapPanel: HTMLElement;
  private readonly mapList: HTMLElement;
  /** Fetched once and reused — the index does not change mid-session. */
  private mapEntries: MapIndexEntry[] | null = null;
  /** Whether a next map exists to advance to (set by the game). */
  private mapSequence = false;
  /** Whether the map controls are offered at all (scripted playback, ?maps=0). */
  private mapControls = true;
  private readonly tweakSteerRow: HTMLElement;
  private readonly tweakSteerBtns: { heading: HTMLButtonElement; rate: HTMLButtonElement };
  private robotBtns!: Record<RobotModelChoice, HTMLButtonElement>;
  private envBtns!: Record<EnvThemeChoice, HTMLButtonElement>;
  private themeChoice: AppearanceThemeId = DEFAULT_APPEARANCE_THEME;
  private appliedTheme: AppearanceThemeId = DEFAULT_APPEARANCE_THEME;
  private robotChoice: RobotModelChoice = "bulldog";
  private skyChoice: SkyChoice = "auto";
  private muted = false;
  private btnMute!: HTMLButtonElement;
  private weatherChoice: WeatherChoice = "auto";
  private envChoice: EnvThemeChoice = "suburban";
  private scheme: ControlScheme = "rate";
  private readonly tweakSensVal: HTMLElement;
  private readonly tweakSpdVal: HTMLElement;
  private floatingTimer: number | null = null;
  private readonly starPop: HTMLElement;
  private starTimer: number | null = null;

  constructor(root: HTMLElement, cb: HudCallbacks) {
    const appearanceThemeButtons = APPEARANCE_THEMES.map(
      (theme) => `<button data-pick="theme:${theme.id}" type="button" role="radio">${theme.label}</button>`,
    ).join("");
    root.innerHTML = `
      <div id="hud-top">
        <!-- Maps first and largest: it is the control a player reaches for,
             and leftmost is the easiest to hit. The gear and the pencil are
             settings and an internal tool. -->
        <div id="hud-controls">
          <button id="btn-maps" type="button" aria-label="Choose a map" title="Choose a map">${icon("map", 26)}</button>
          <button id="btn-tweak" type="button" aria-label="Tweak controls" title="Tweak controls">${icon("settings")}</button>
          <button id="btn-editor" type="button" aria-label="Open map editor" title="Map editor">${icon("edit")}</button>
        </div>
        <div id="hud-centre">
          <div id="hud-title"></div>
          <div id="hud-hint"></div>
          <div id="mission-distance" class="hidden"></div>
        </div>
        <div id="hud-corner">
          <canvas id="minimap" class="hidden" width="120" height="120" aria-label="Route map"></canvas>
          <div id="app-version"></div>
        </div>
        <div id="guidance" class="hidden" role="status" aria-live="polite">
          <span id="guidance-icon"></span><span id="guidance-text"></span>
        </div>
      </div>
      <div id="tweak-panel" class="hidden" role="dialog" aria-label="Tweak controls">
        <div class="tweak-title">Tweak</div>
        <label class="tweak-row">
          <span>Steering sensitivity</span>
          <input id="tweak-sens" type="range" min="${TUNING_SENSITIVITY_RANGE.min}" max="${TUNING_SENSITIVITY_RANGE.max}" step="${TUNING_SENSITIVITY_RANGE.step}" />
          <output id="tweak-sens-val"></output>
        </label>
        <label class="tweak-row">
          <span>Speed</span>
          <input id="tweak-spd" type="range" min="${TUNING_SPEED_RANGE.min}" max="${TUNING_SPEED_RANGE.max}" step="${TUNING_SPEED_RANGE.step}" />
          <output id="tweak-spd-val"></output>
        </label>
        <div class="tweak-row tweak-steer-row" id="tweak-steer-row">
          <span>Steering</span>
          <div id="tweak-steer" role="radiogroup" aria-label="Steering model">
            <button id="steer-rate" type="button" role="radio" aria-checked="true">Racing</button>
            <button id="steer-heading" type="button" role="radio" aria-checked="false">Direct</button>
          </div>
        </div>
        <div class="tweak-note tweak-steer-note">Racing: left/right steer, up/down is throttle and brake. Direct: the stick points where to go.</div>
        <div class="tweak-row tweak-steer-row" id="tweak-robot-row">
          <span>Robot</span>
          <div role="radiogroup" aria-label="Robot model">
            <button id="robot-bulldog" type="button" role="radio" aria-checked="true">Asimov 04</button>
            <button id="robot-alaska" type="button" role="radio" aria-checked="false">Alaska</button>
            <button id="robot-box" type="button" role="radio" aria-checked="false">Classic</button>
          </div>
        </div>
        <div class="tweak-row tweak-steer-row" id="tweak-env-row">
          <span>Setting</span>
          <div role="radiogroup" aria-label="World setting">
            <button id="env-suburban" type="button" role="radio" aria-checked="true">Open district</button>
            <button id="env-downtown" type="button" role="radio" aria-checked="false">Downtown</button>
          </div>
        </div>
        <div class="quick-row theme-picker" role="radiogroup" aria-label="Visual style">
          <span class="quick-label">Theme</span>
          ${appearanceThemeButtons}
        </div>
        <div class="tweak-note theme-description">One theme for the whole world.</div>
        <div class="tweak-note">Saved settings apply on the next attempt (the current run restarts).</div>
        <div id="tweak-buttons">
          <button id="btn-tweak-save" type="button">Save &amp; restart</button>
          <button id="btn-tweak-reset" type="button">Reset defaults</button>
          <button id="btn-tweak-close" type="button">Cancel</button>
        </div>
      </div>
      <div id="start-panel" class="hidden" role="dialog" aria-label="Choose a map to drive">
        <div id="start-eyebrow">Today you are driving</div>
        <div id="start-map"></div>
        <div id="start-desc"></div>
        <div id="start-buttons">
          <button id="btn-start" type="button">${icon("play", 20)}<span>Drive this map</span></button>
          <button id="btn-start-random" type="button">${icon("shuffle", 20)}<span>Give me another</span></button>
          <button id="btn-start-choose" type="button">${icon("list", 20)}<span>Choose from the list</span></button>
        </div>
        <div class="quick-row" role="radiogroup" aria-label="Robot model">
          <span class="quick-label">Robot</span>
          <button data-pick="robot:bulldog" type="button" role="radio">Asimov 04</button>
          <button data-pick="robot:alaska" type="button" role="radio">Alaska</button>
          <button data-pick="robot:box" type="button" role="radio">Classic</button>
        </div>
        <div class="quick-row" role="radiogroup" aria-label="World setting">
          <span class="quick-label">World</span>
          <button data-pick="env:suburban" type="button" role="radio">Open district</button>
          <button data-pick="env:downtown" type="button" role="radio">Downtown</button>
        </div>
        <div class="quick-row" role="radiogroup" aria-label="Time of day">
          <span class="quick-label">Light</span>
          <button data-pick="sky:auto" type="button" role="radio">Auto</button>
          <button data-pick="sky:day" type="button" role="radio">Day</button>
          <button data-pick="sky:dusk" type="button" role="radio">Dusk</button>
          <button data-pick="sky:night" type="button" role="radio">Night</button>
        </div>
        <div class="quick-row" role="radiogroup" aria-label="Weather">
          <span class="quick-label">Weather</span>
          <button data-pick="weather:auto" type="button" role="radio">Auto</button>
          <button data-pick="weather:clear" type="button" role="radio">Clear</button>
          <button data-pick="weather:rain" type="button" role="radio">Wet</button>
        </div>
        <div class="quick-row theme-picker" role="radiogroup" aria-label="Visual style">
          <span class="quick-label">Theme</span>
          ${appearanceThemeButtons}
        </div>
        <div class="tweak-note theme-description">One theme for the whole world.</div>
        <div id="start-note">Your route is recorded, to train the robot.</div>
      </div>
      <div id="map-panel" class="hidden" role="dialog" aria-label="Choose a map">
        <div class="tweak-title">Maps<span id="map-count"></span></div>
        <div id="map-list"></div>
        <div class="quick-row" role="radiogroup" aria-label="Robot model">
          <span class="quick-label">Robot</span>
          <button data-pick="robot:bulldog" type="button" role="radio">Asimov 04</button>
          <button data-pick="robot:alaska" type="button" role="radio">Alaska</button>
          <button data-pick="robot:box" type="button" role="radio">Classic</button>
        </div>
        <div class="quick-row" role="radiogroup" aria-label="World setting">
          <span class="quick-label">World</span>
          <button data-pick="env:suburban" type="button" role="radio">Open district</button>
          <button data-pick="env:downtown" type="button" role="radio">Downtown</button>
        </div>
        <div class="quick-row" role="radiogroup" aria-label="Time of day">
          <span class="quick-label">Light</span>
          <button data-pick="sky:auto" type="button" role="radio">Auto</button>
          <button data-pick="sky:day" type="button" role="radio">Day</button>
          <button data-pick="sky:dusk" type="button" role="radio">Dusk</button>
          <button data-pick="sky:night" type="button" role="radio">Night</button>
        </div>
        <div class="quick-row" role="radiogroup" aria-label="Weather">
          <span class="quick-label">Weather</span>
          <button data-pick="weather:auto" type="button" role="radio">Auto</button>
          <button data-pick="weather:clear" type="button" role="radio">Clear</button>
          <button data-pick="weather:rain" type="button" role="radio">Wet</button>
        </div>
        <div class="quick-row theme-picker" role="radiogroup" aria-label="Visual style">
          <span class="quick-label">Theme</span>
          ${appearanceThemeButtons}
        </div>
        <div class="tweak-note theme-description">One theme for the whole world.</div>
        <div class="tweak-note">Changing the map or theme restarts the run. Seed and settings are kept.</div>
        <div id="map-buttons"><button id="btn-maps-close" type="button">Close</button></div>
      </div>
      <div id="floating-feedback" class="hidden"></div>
      <!-- Bottom-right, above the joystick band: the reachable corner for a
           right thumb that is not already holding the stick, and out of the
           top strip, which has no room left at 390 px. -->
      <button id="btn-view" type="button" aria-label="Switch camera view" title="Camera view (V)"></button>
      <!-- Sound, directly under the camera button: the two things a player
           reaches for mid-run that are not the stick. -->
      <button id="btn-mute" type="button" aria-label="Sound on or off" title="Sound (M)"></button>
      <!-- Live lane discipline (ADR-052). Bottom-left, clear of the stick:
           the one instrument a delivery driver actually needs — where am I in
           the lane, and is that where I should be? -->
      <div id="lane-gauge" class="hidden" aria-live="off">
        <div id="lane-label">In lane</div>
        <div id="lane-bar"><div id="lane-zone"></div><div id="lane-marker"></div></div>
      </div>
      <!-- Speed and steering, stacked above the lane gauge (ADR-073). The
           lane gauge answers "where am I"; this answers "what am I doing" —
           the two halves of watching yourself drive. Left column, clear of
           the stick and of the right-hand buttons. -->
      <div id="drive-readout" class="hidden" aria-live="off">
        <div class="ro-top">
          <span id="ro-speed">0.0</span><span class="ro-unit">m/s</span>
          <span id="ro-kmh">0 km/h</span>
        </div>
        <div class="ro-bar"><div id="ro-speed-fill"></div></div>
        <div class="ro-steer">
          <div class="ro-track"><div class="ro-centre"></div><div id="ro-steer-marker"></div></div>
          <span id="ro-yaw">0°/s</span>
        </div>
      </div>
      <!-- Beside the view toggle, because they are the same kind of control:
           what the camera does. Hidden entirely on a browser that cannot
           record, rather than offered and then failing on the press. -->
      <button id="btn-record" type="button" class="hidden" aria-label="Record the driver's view" title="Record the driver's view">${icon("record", 22)}</button>
      <div id="record-panel" class="hidden" role="dialog" aria-label="Record the driver's view">
        <div class="tweak-title">Record the drive</div>
        <label class="tweak-row record-row">
          <span>File name</span>
          <input id="record-name" type="text" spellcheck="false" autocomplete="off" placeholder="streetsense" />
        </label>
        <div class="tweak-note" id="record-note"></div>
        <div id="record-buttons">
          <button id="btn-record-arm" type="button"></button>
          <button id="btn-record-close" type="button">Close</button>
        </div>
      </div>
      <div id="collision-warning" class="hidden" role="alert" aria-live="assertive"></div>
      <div id="star-pop" class="hidden" aria-hidden="true"></div>
      <div id="end-panel" class="hidden">
        <div id="end-text"></div>
        <div id="end-report" class="hidden"></div>
        <!-- Ordered by what the player most likely wants next, not by the
             order the buttons were added. Carrying on to the next map is the
             point of a sequence; Download JSON is a developer's button and
             sits last so it is not the loudest thing on a finished run. -->
        <div id="end-buttons">
          <button id="btn-next-map" type="button"><span>Next map</span>${icon("arrowRight", 20)}</button>
          <button id="btn-next" type="button">Next</button>
          <div id="end-buttons-row">
            <button id="btn-retry" type="button">${icon("retry", 18)}<span>Retry</span></button>
            <button id="btn-end-maps" type="button">${icon("map", 18)}<span>Other maps</span></button>
          </div>
          <button id="btn-download" type="button">${icon("download", 15)}<span>Download JSON</span></button>
        </div>
        <!-- Also said on the welcome card, but somebody arriving by a direct
             link never sees that one, and "we kept your route" is not a thing
             to mention in only some of the ways in. The wording follows what
             actually happened rather than asserting it up front — see
             setSaveState(). -->
        <div id="end-note"></div>
      </div>
      <div id="rotate-overlay" class="hidden">Please rotate your device to portrait.</div>`;

    this.root = root;
    this.title = root.querySelector("#hud-title") as HTMLElement;
    this.hint = root.querySelector("#hud-hint") as HTMLElement;
    this.distance = root.querySelector("#mission-distance") as HTMLElement;
    this.minimap = root.querySelector("#minimap") as HTMLCanvasElement;
    this.minimapCtx = this.minimap.getContext("2d");
    this.guidance = root.querySelector("#guidance") as HTMLElement;
    this.guidanceIcon = root.querySelector("#guidance-icon") as HTMLElement;
    this.guidanceText = root.querySelector("#guidance-text") as HTMLElement;
    this.floating = root.querySelector("#floating-feedback") as HTMLElement;
    this.starPop = root.querySelector("#star-pop") as HTMLElement;
    this.panel = root.querySelector("#end-panel") as HTMLElement;
    this.panelText = root.querySelector("#end-text") as HTMLElement;
    this.panelReport = root.querySelector("#end-report") as HTMLElement;
    this.warningEl = root.querySelector("#collision-warning") as HTMLElement;
    this.btnNext = root.querySelector("#btn-next") as HTMLButtonElement;

    this.tweakPanel = root.querySelector("#tweak-panel") as HTMLElement;
    this.tweakSens = root.querySelector("#tweak-sens") as HTMLInputElement;
    this.tweakSpd = root.querySelector("#tweak-spd") as HTMLInputElement;
    this.tweakSensVal = root.querySelector("#tweak-sens-val") as HTMLElement;
    this.tweakSteerRow = root.querySelector("#tweak-steer-row") as HTMLElement;
    this.tweakSteerBtns = {
      rate: root.querySelector("#steer-rate") as HTMLButtonElement,
      heading: root.querySelector("#steer-heading") as HTMLButtonElement,
    };
    for (const s of ["heading", "rate"] as const) {
      this.tweakSteerBtns[s].addEventListener("click", () => this.setScheme(s));
    }
    this.robotBtns = {
      bulldog: root.querySelector("#robot-bulldog") as HTMLButtonElement,
      alaska: root.querySelector("#robot-alaska") as HTMLButtonElement,
      box: root.querySelector("#robot-box") as HTMLButtonElement,
    };
    for (const r of ["bulldog", "alaska", "box"] as const) {
      this.robotBtns[r].addEventListener("click", () => this.setRobot(r));
    }
    this.envBtns = {
      suburban: root.querySelector("#env-suburban") as HTMLButtonElement,
      downtown: root.querySelector("#env-downtown") as HTMLButtonElement,
    };
    for (const e of ["suburban", "downtown"] as const) {
      this.envBtns[e].addEventListener("click", () => this.setEnv(e));
    }
    this.tweakSpdVal = root.querySelector("#tweak-spd-val") as HTMLElement;
    const refreshTweakLabels = (): void => {
      this.tweakSensVal.textContent = `${Number(this.tweakSens.value).toFixed(2)}×`;
      this.tweakSpdVal.textContent = `${Number(this.tweakSpd.value).toFixed(2)}×`;
    };
    this.tweakSens.addEventListener("input", refreshTweakLabels);
    this.tweakSpd.addEventListener("input", refreshTweakLabels);
    (root.querySelector("#btn-tweak") as HTMLButtonElement).addEventListener("click", () => {
      this.setAppearanceTheme(this.appliedTheme);
      this.tweakPanel.classList.toggle("hidden");
    });
    (root.querySelector("#btn-tweak-close") as HTMLButtonElement).addEventListener("click", () => {
      this.setAppearanceTheme(this.appliedTheme);
      this.tweakPanel.classList.add("hidden");
    });
    (root.querySelector("#btn-tweak-save") as HTMLButtonElement).addEventListener("click", () => {
      this.tweakPanel.classList.add("hidden");
      cb.onTuningSave(
        {
          sensitivity: Number(this.tweakSens.value),
          speedScale: Number(this.tweakSpd.value),
        },
        this.scheme,
        this.robotChoice,
        this.envChoice,
        this.themeChoice,
      );
    });
    (root.querySelector("#btn-tweak-reset") as HTMLButtonElement).addEventListener("click", () => {
      this.setAppearanceTheme(this.appliedTheme);
      this.setTuning({ ...TUNING_DEFAULTS });
      this.tweakPanel.classList.add("hidden");
      cb.onTuningReset();
    });

    this.btnEditor = root.querySelector("#btn-editor") as HTMLButtonElement;
    this.btnEditor.addEventListener("click", cb.onOpenEditor);

    this.btnView = root.querySelector("#btn-view") as HTMLButtonElement;
    this.btnView.addEventListener("click", () => {
      playTick("on");
      cb.onToggleView();
    });
    // Desktop: the same action on a key, because reaching for a corner button
    // with a mouse while steering with the keyboard is nobody's idea of
    // convenient.
    window.addEventListener("keydown", (e) => {
      if (e.key === "v" || e.key === "V") cb.onToggleView();
    });
    this.btnMute = root.querySelector("#btn-mute") as HTMLButtonElement;
    this.btnMute.addEventListener("click", () => {
      const next = !this.muted;
      // Tick BEFORE muting, so switching sound off still acknowledges the
      // press; after unmuting, so the press that restores sound is heard.
      if (next) playTick("off");
      this.setMute(next);
      cb.onMute(next);
      if (!next) playTick("on");
    });
    window.addEventListener("keydown", (e) => {
      if (e.key === "m" || e.key === "M") {
        const next = !this.muted;
        if (next) playTick("off");
        this.setMute(next);
        cb.onMute(next);
        if (!next) playTick("on");
      }
    });
    this.btnRecord = root.querySelector("#btn-record") as HTMLButtonElement;
    this.recordPanel = root.querySelector("#record-panel") as HTMLElement;
    this.recordName = root.querySelector("#record-name") as HTMLInputElement;
    this.recordNote = root.querySelector("#record-note") as HTMLElement;
    this.btnRecordArm = root.querySelector("#btn-record-arm") as HTMLButtonElement;
    this.btnRecord.addEventListener("click", () => {
      // Armed already: one press stops, because hunting through a dialog to
      // stop a recording is the wrong thing to ask of someone mid-session.
      if (this.recordArmed) this.setRecordArmed(false, cb);
      else this.recordPanel.classList.remove("hidden");
    });
    this.btnRecordArm.addEventListener("click", () => {
      this.setRecordArmed(!this.recordArmed, cb);
      if (this.recordArmed) this.recordPanel.classList.add("hidden");
    });
    (root.querySelector("#btn-record-close") as HTMLButtonElement).addEventListener("click", () => {
      this.recordPanel.classList.add("hidden");
    });
    this.renderRecord();

    // Quick pickers. One selector wires every copy (start card, map panel),
    // and the setters repaint them all, so the panels can never disagree with
    // each other or with the Tweak panel. Wired ONCE: a second identical loop
    // had crept in, so every pick fired its callback twice and restarted the
    // attempt twice.
    for (const b of Array.from(root.querySelectorAll<HTMLButtonElement>("[data-pick]"))) {
      b.addEventListener("click", () => {
        const [kind, value] = (b.dataset["pick"] ?? "").split(":");
        if (kind === "robot") {
          this.setRobot(value as RobotModelChoice);
          cb.onRobotModel(value as RobotModelChoice);
        } else if (kind === "env") {
          this.setEnv(value as EnvThemeChoice);
          cb.onEnvTheme(value as EnvThemeChoice);
        } else if (kind === "sky") {
          this.setSky(value as SkyChoice);
          cb.onSky(value as SkyChoice);
        } else if (kind === "weather") {
          this.setWeather(value as WeatherChoice);
          cb.onWeather(value as WeatherChoice);
        } else if (kind === "theme") {
          const id = value as AppearanceThemeId;
          if (b.closest("#tweak-panel")) {
            this.themeChoice = id;
            this.paintQuick("theme", id, this.tweakPanel);
          } else {
            this.setAppearanceTheme(id);
            cb.onAppearanceTheme(id);
          }
        }
      });
    }

    this.endNote = root.querySelector("#end-note") as HTMLElement;
    this.startPanel = root.querySelector("#start-panel") as HTMLElement;
    this.startMap = root.querySelector("#start-map") as HTMLElement;
    this.startDesc = root.querySelector("#start-desc") as HTMLElement;
    (root.querySelector("#btn-start") as HTMLButtonElement).addEventListener("click", () => {
      this.hideStart();
      cb.onStart();
    });
    (root.querySelector("#btn-start-random") as HTMLButtonElement).addEventListener("click", () => {
      cb.onRandomMap();
    });
    (root.querySelector("#btn-start-choose") as HTMLButtonElement).addEventListener("click", () => {
      void this.openMaps();
    });
    this.btnNextMap = root.querySelector("#btn-next-map") as HTMLButtonElement;
    this.btnNextMap.addEventListener("click", cb.onNextMap);

    this.mapPanel = root.querySelector("#map-panel") as HTMLElement;
    this.mapList = root.querySelector("#map-list") as HTMLElement;
    this.btnMaps = root.querySelector("#btn-maps") as HTMLButtonElement;
    this.btnEndMaps = root.querySelector("#btn-end-maps") as HTMLButtonElement;
    for (const b of [this.btnMaps, this.btnEndMaps]) {
      b.addEventListener("click", () => {
        void this.openMaps();
      });
    }
    (root.querySelector("#btn-maps-close") as HTMLButtonElement).addEventListener("click", () => {
      this.mapPanel.classList.add("hidden");
    });

    (root.querySelector("#btn-retry") as HTMLButtonElement).addEventListener("click", cb.onRetry);
    this.btnNext.addEventListener("click", cb.onNext);
    (root.querySelector("#btn-download") as HTMLButtonElement).addEventListener(
      "click",
      cb.onDownload,
    );

    const rotate = root.querySelector("#rotate-overlay") as HTMLElement;
    const checkOrientation = (): void => {
      const landscape = window.innerWidth > window.innerHeight;
      const touch = window.matchMedia("(pointer: coarse)").matches;
      rotate.classList.toggle("hidden", !(landscape && touch));
    };
    window.addEventListener("resize", checkOrientation);
    window.visualViewport?.addEventListener("resize", checkOrientation);
    checkOrientation();
  }

  setLevel(name: string, hint: string): void {
    this.title.textContent = name;
    this.hint.textContent = hint;
  }

  setHint(hint: string): void {
    this.hint.textContent = hint;
  }

  /** Reflect the tuning in force on the Tweak sliders. */
  setTuning(t: Tuning): void {
    this.tweakSens.value = String(t.sensitivity);
    this.tweakSpd.value = String(t.speedScale);
    this.tweakSensVal.textContent = `${t.sensitivity.toFixed(2)}×`;
    this.tweakSpdVal.textContent = `${t.speedScale.toFixed(2)}×`;
  }

  /**
   * Show the selected steering model (ADR-023). Only the mission offers the
   * choice; the lab levels are the pinned research instrument and always run
   * the heading model, so the row is hidden there.
   */
  /**
   * Drive the lane gauge. `null` hides it (lab levels have no kerbs to keep
   * to). Distance maps 0..4 m across the bar; the tinted zone is the target.
   */
  /**
   * Speed and steering, ~16 Hz (ADR-073).
   *
   * `yawRate` rather than a steering angle: the robot is a differential
   * drive, so it has no steered wheel to report an angle for — what it
   * actually has, and what a driver feels, is a rate of turn.
   */
  setReadout(speedMps: number | null, yawRateRad: number, cruiseMps: number): void {
    const box = document.getElementById("drive-readout");
    if (!box) return;
    if (speedMps === null || !Number.isFinite(speedMps)) {
      box.classList.add("hidden");
      return;
    }
    box.classList.remove("hidden");

    const v = Math.max(0, speedMps);
    document.getElementById("ro-speed")!.textContent = v.toFixed(1);
    document.getElementById("ro-kmh")!.textContent = `${(v * 3.6).toFixed(1)} km/h`;
    const cruise = Math.max(cruiseMps, 0.5);
    const fill = document.getElementById("ro-speed-fill")!;
    fill.style.width = `${Math.min(100, (v / cruise) * 100).toFixed(1)}%`;
    // Over the level's cruise speed is worth seeing, not just clipping at
    // full: the bar turns amber rather than growing past the track.
    fill.dataset["over"] = v > cruise * 1.02 ? "1" : "0";

    const deg = (Number.isFinite(yawRateRad) ? yawRateRad : 0) * (180 / Math.PI);
    document.getElementById("ro-yaw")!.textContent = `${deg >= 0 ? "" : "\u2212"}${Math.abs(deg).toFixed(0)}\u00b0/s`;
    // Scale to the profile's 1.2 rad/s yaw cap (~69 deg/s), which driving
    // really does reach — measured across four maps, full lock came out at
    // 1.155 rad/s. But the median turn is only 0.215 rad/s, which on a linear
    // track is a 17% twitch and reads as a needle that never moves. So the
    // POSITION is eased with a 0.7 gamma, which lifts an ordinary turn to
    // about a third of the track while full lock still reaches the end. The
    // NUMBER beside it stays exact — the curve is for the eye, not the data.
    const norm = Math.max(-1, Math.min(1, deg / 69));
    const frac = Math.sign(norm) * Math.abs(norm) ** 0.7;
    const marker = document.getElementById("ro-steer-marker")!;
    marker.style.left = `${(50 + frac * 46).toFixed(1)}%`;
    marker.dataset["hard"] = Math.abs(frac) > 0.72 ? "1" : "0";
  }

  setLane(lane: { nearside: number; wrongSide: boolean } | null): void {
    const g = document.getElementById("lane-gauge");
    if (!g) return;
    if (!lane || !Number.isFinite(lane.nearside)) {
      g.classList.add("hidden");
      return;
    }
    g.classList.remove("hidden");
    const label = document.getElementById("lane-label")!;
    const marker = document.getElementById("lane-marker")!;
    // The kerb is the RIGHT end of the gauge (the green zone sits there), so
    // the marker moves right as the robot keeps right — play-testing found
    // the mirrored layout counter-intuitive.
    const frac = Math.min(1, lane.nearside / 4);
    marker.style.left = `${((1 - frac) * 100).toFixed(1)}%`;
    const state = lane.wrongSide ? "wrong" : lane.nearside <= 1.5 ? "good" : "adrift";
    if (state !== this.laneStateShown) {
      this.laneStateShown = state;
      g.dataset["state"] = state;
      label.textContent =
        state === "wrong" ? "Wrong side" : state === "good" ? "In lane" : "Keep in";
    }
  }

  private laneStateShown = "";

  /** Reflect (and remember) the machine choice in the tweak panel. */
  setRobot(r: RobotModelChoice): void {
    this.robotChoice = r;
    for (const k of ["bulldog", "alaska", "box"] as const) {
      const on = k === r;
      this.robotBtns[k].classList.toggle("on", on);
      this.robotBtns[k].setAttribute("aria-checked", on ? "true" : "false");
    }
    this.paintQuick("robot", r);
  }

  /** Repaint every quick-picker copy of one choice group. */
  private paintQuick(kind: string, value: string, scope: ParentNode = document): void {
    for (const b of Array.from(scope.querySelectorAll<HTMLButtonElement>(`[data-pick^="${kind}:"]`))) {
      const on = b.dataset["pick"] === `${kind}:${value}`;
      b.classList.toggle("on", on);
      b.setAttribute("aria-checked", on ? "true" : "false");
    }
  }

  /** Reflect (and remember) the world-setting choice. */
  setEnv(e: EnvThemeChoice): void {
    this.envChoice = e;
    for (const k of ["suburban", "downtown"] as const) {
      const on = k === e;
      this.envBtns[k].classList.toggle("on", on);
      this.envBtns[k].setAttribute("aria-checked", on ? "true" : "false");
    }
    this.paintQuick("env", e);
  }

  setMute(on: boolean): void {
    this.muted = on;
    this.btnMute.innerHTML = icon(on ? "soundOff" : "sound", 22);
    this.btnMute.classList.toggle("off", on);
    this.btnMute.setAttribute("aria-pressed", on ? "true" : "false");
  }

  setSky(v: SkyChoice): void {
    this.skyChoice = v;
    this.paintQuick("sky", v);
  }

  setWeather(v: WeatherChoice): void {
    this.weatherChoice = v;
    this.paintQuick("weather", v);
  }


  setAppearanceTheme(id: AppearanceThemeId): void {
    this.themeChoice = APPEARANCE_THEMES.some((theme) => theme.id === id) ? id : DEFAULT_APPEARANCE_THEME;
    this.appliedTheme = this.themeChoice;
    this.paintQuick("theme", this.themeChoice);
  }
  setScheme(s: ControlScheme, selectable = true): void {
    this.scheme = s;
    this.tweakSteerRow.classList.toggle("hidden", !selectable);
    for (const k of ["heading", "rate"] as const) {
      const on = k === s;
      this.tweakSteerBtns[k].classList.toggle("on", on);
      this.tweakSteerBtns[k].setAttribute("aria-checked", on ? "true" : "false");
    }
  }

  /**
   * Show or hide the map-editor button. Hidden during scripted playback so
   * it never appears in a recording, and hideable outright once the game
   * goes in front of participants.
   */
  setEditorVisible(show: boolean): void {
    this.btnEditor.classList.toggle("hidden", !show);
  }

  /**
   * Show or hide the map-picker button.
   *
   * Hidden during scripted playback for the same reason as the editor button
   * (a control that nobody pressed has no business in a recording), and
   * switchable with `?maps=0` for a controlled session, where the map is part
   * of the condition and a participant must not be able to change it
   * mid-study.
   */
  setMapsVisible(show: boolean): void {
    this.btnMaps.classList.toggle("hidden", !show);
    if (!show) this.mapPanel.classList.add("hidden");
  }

  /**
   * Whether the END PANEL may offer other maps.
   *
   * A separate switch from the top-strip button on purpose. That one hides
   * during scripted playback because it would sit on screen through a
   * deliverable recording; the end panel appears after the driving is over,
   * where a way onward is useful rather than intrusive. Both go away for
   * `?maps=0`, where the map is part of a controlled condition.
   */
  setMapControls(allowed: boolean): void {
    this.mapControls = allowed;
    this.btnEndMaps.style.display = allowed ? "" : "none";
  }

  /**
   * A star earned by lane driving (ADR-065 amendment E): a small pop that
   * rises and fades where the robot sits on screen. Restarting the CSS
   * animation needs the reflow nudge; combos of five and up go gold.
   */
  popStar(points: number, combo: number): void {
    this.starPop.textContent = combo > 1 ? `\u2605 +${points} \u00D7${combo}` : `\u2605 +${points}`;
    this.starPop.classList.toggle("star-gold", combo >= 5);
    this.starPop.classList.remove("hidden", "star-run");
    void this.starPop.offsetWidth;
    this.starPop.classList.add("star-run");
    if (this.starTimer !== null) window.clearTimeout(this.starTimer);
    this.starTimer = window.setTimeout(() => this.starPop.classList.add("hidden"), 900);
  }

  /** Small floating mission-distance text near the top (never near the joystick). */
  setDistance(meters: number | null): void {
    if (meters === null) {
      this.distance.classList.add("hidden");
      return;
    }
    this.distance.classList.remove("hidden");
    this.distance.textContent = `${Math.max(0, Math.ceil(meters))} m to go`;
  }

  /** Build version and when it was last updated, small in the top strip. */
  setVersion(v: string, builtAtIso = ""): void {
    let text = "v" + v;
    const d = builtAtIso ? new Date(builtAtIso) : null;
    if (d && !Number.isNaN(d.getTime())) {
      const day = d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
      const time = d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
      text += ` · ${day} ${time}`;
    }
    (this.root.querySelector("#app-version") as HTMLElement).textContent = text;
  }

  /**
   * Minimap (mission only): the road network, the delivery route, the customer
   * and where you are. Deliberately shows NO other road users — a map that
   * marked the oncoming pedestrian would cue the passing-side decision this
   * study measures.
   */
  buildMinimap(net: RoadNetwork, path: DeliveryPath, dest: { x: number; z: number }): void {
    const size = this.minimap.width;
    const b = net.bounds;
    const pad = 6;
    const k = Math.min((size - 2 * pad) / (b.maxX - b.minX), (size - 2 * pad) / (b.maxZ - b.minZ));
    const ox = pad + ((size - 2 * pad) - (b.maxX - b.minX) * k) / 2 - b.minX * k;
    const oz = pad + ((size - 2 * pad) - (b.maxZ - b.minZ) * k) / 2 - b.minZ * k;
    this.minimapFit = { ox, oz, k };

    const base = document.createElement("canvas");
    base.width = size;
    base.height = size;
    const c = base.getContext("2d");
    if (!c) return;
    // z grows north; screen y grows down, so flip z.
    const X = (x: number): number => ox + x * k;
    const Y = (z: number): number => size - (oz + z * k);

    c.lineCap = "round";
    c.lineJoin = "round";
    for (const p of net.pieces) {
      if (p.kind === "band" && p.frame === false) continue;
      c.strokeStyle = "rgba(120,124,132,0.85)";
      if (p.kind === "band") {
        c.lineWidth = Math.max(2, p.halfWidth * 2 * k);
        c.beginPath();
        p.pts.forEach((q, i) => (i ? c.lineTo(X(q.x), Y(q.z)) : c.moveTo(X(q.x), Y(q.z))));
        c.stroke();
      } else {
        c.lineWidth = Math.max(2, (p.rOut - p.rIn) * k);
        c.beginPath();
        c.arc(X(p.cx), Y(p.cz), ((p.rIn + p.rOut) / 2) * k, 0, Math.PI * 2);
        c.stroke();
      }
    }
    // The route itself, on top.
    c.strokeStyle = "rgba(245,183,49,0.95)";
    c.lineWidth = 2;
    c.beginPath();
    for (let s = 0; s <= path.length; s += 2) {
      const p = path.point(s);
      if (s === 0) c.moveTo(X(p.x), Y(p.z));
      else c.lineTo(X(p.x), Y(p.z));
    }
    c.stroke();
    // Every junction on the route, so a player who has driven past one can
    // see where it was and steer back to it — the guidance line only ever
    // talks about the turn ahead.
    c.strokeStyle = "#e08a1e";
    c.lineWidth = 1.6;
    for (const turn of path.turns) {
      if (turn.kind === "arrive") continue;
      const p = path.point(turn.s);
      c.beginPath();
      c.arc(X(p.x), Y(p.z), 3, 0, Math.PI * 2);
      c.stroke();
    }
    // Customer.
    c.fillStyle = "#2e7d32";
    c.beginPath();
    c.arc(X(dest.x), Y(dest.z), 3.5, 0, Math.PI * 2);
    c.fill();

    this.minimapBase = base;
    this.minimap.classList.remove("hidden");
  }

  /** Per-frame: base map + the robot's position and facing. */
  updateMinimap(x: number, z: number, heading: number): void {
    const ctx = this.minimapCtx;
    const fit = this.minimapFit;
    const base = this.minimapBase;
    if (!ctx || !fit || !base) return;
    const size = this.minimap.width;
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(base, 0, 0);
    const px = fit.ox + x * fit.k;
    const py = size - (fit.oz + z * fit.k);
    ctx.save();
    ctx.translate(px, py);
    ctx.rotate(-heading); // screen y is flipped, so negate
    ctx.fillStyle = "#1b3a8f";
    ctx.beginPath();
    ctx.moveTo(0, -5.5);
    ctx.lineTo(3.5, 3.5);
    ctx.lineTo(-3.5, 3.5);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  hideMinimap(): void {
    this.minimap.classList.add("hidden");
  }

  /** Turn-by-turn guidance line in the top strip (mission only). */
  setGuidance(g: Guidance | null, mirror = false): void {
    if (!g || g.kind === "straight") {
      this.guidance.classList.add("hidden");
      this.lastGuidanceKey = "";
      return;
    }
    const icon =
      g.kind === "left"
        ? "\u2B05"
        : g.kind === "right"
          ? "\u27A1"
          : g.kind === "roundabout"
            ? mirror
              ? "\u21BB" // clockwise ring on the mirrored map
              : "\u21BA" // counter-clockwise (right-hand traffic)
            : g.kind === "arrive"
              ? "\uD83D\uDCCD"
              : "\u21A9";
    // Distance in 5 m steps: the live region must not re-announce every metre.
    const dist =
      g.distance !== null ? ` \u00B7 ${Math.max(0, Math.ceil(g.distance / 5) * 5)} m` : "";
    const text = `${g.label}${dist}`;
    const key = `${g.kind}|${text}`;
    if (key === this.lastGuidanceKey) return;
    this.lastGuidanceKey = key;
    this.guidance.classList.remove("hidden");
    this.guidance.dataset["kind"] = g.kind;
    this.guidanceIcon.textContent = icon;
    this.guidanceText.textContent = text;
  }

  /** Brief centered feedback text ("Smooth") that fades on its own. */
  showFloatingText(text: string, tone: "smooth" | "awkward"): void {
    this.floating.textContent = text;
    this.floating.className = `feedback-${tone}`;
    if (this.floatingTimer !== null) window.clearTimeout(this.floatingTimer);
    this.floatingTimer = window.setTimeout(() => {
      this.floating.classList.add("hidden");
    }, 1400);
  }

  /**
   * Open the map picker, fetching the index the first time it is asked for.
   *
   * The list is built from `maps/index.json`, so the set of maps on offer is
   * whatever is in `public/maps/` — see mapPicker.ts.
   */
  private async openMaps(): Promise<void> {
    this.mapPanel.classList.remove("hidden");
    if (this.mapEntries === null) {
      this.mapList.textContent = "Loading…";
      this.mapEntries = await fetchMapIndex();
    }
    const search = window.location.search;
    const current = currentMapId(search);
    const real = realMapsEnabled(search);
    // Count what the list actually shows, not what the index holds: saying 29
    // above a list of 22 reads as a bug in the list.
    const shown = this.mapEntries.filter((e) => real || !e.photographic || e.id === current);
    const count = this.root.querySelector("#map-count");
    if (count) count.textContent = shown.length ? ` (${shown.length})` : "";
    renderMapList(this.mapList, this.mapEntries, current, (id) => {
      window.location.search = mapUrl(window.location.search, id);
    }, real);
    this.renderRealMapsToggle(real, this.mapEntries.filter((e) => e.photographic).length);
  }

  /**
   * The switch that brings the real-street reconstructions into the list.
   *
   * They are off by default because each is tens of megabytes of Gaussians,
   * and somebody opening the simulator to drive a scenario should not wait for
   * a photographic street they did not ask for. The switch is in the picker
   * rather than only in the URL so it can be found without reading the docs.
   */
  private renderRealMapsToggle(on: boolean, available: number): void {
    if (available === 0) return;
    const row = document.createElement("div");
    row.className = "map-real-toggle";
    const b = document.createElement("button");
    b.type = "button";
    b.className = "map-real-toggle-button";
    b.textContent = on
      ? `Hide the ${available} real-street maps`
      : `Show the ${available} real-street maps`;
    b.title = on
      ? "Back to the built scenarios, which open immediately"
      : "Photographic reconstructions: tens of megabytes each, slower to open";
    b.addEventListener("click", () => {
      window.location.search = realMapsUrl(window.location.search, !on);
    });
    row.appendChild(b);
    this.mapList.appendChild(row);
  }

  /**
   * The welcome card: which map a fresh visitor landed on, and the three ways
   * out of it. Shown only on a bare visit — any explicit query means somebody
   * asked for something specific and a card in the way would be rude.
   */
  showStart(name: string, description: string): void {
    this.startMap.textContent = name;
    this.startDesc.textContent = description;
    this.startPanel.classList.remove("hidden");
  }

  hideStart(): void {
    this.startPanel.classList.add("hidden");
  }

  get startShown(): boolean {
    return !this.startPanel.classList.contains("hidden");
  }

  /**
   * What became of the run's upload.
   *
   * The panel used to read "This route was saved" the moment it opened, which
   * was a claim about something that had not finished happening and might not
   * succeed at all. On a phone with no signal it was simply false. It now
   * reports the state it is actually in.
   */
  setSaveState(state: "saving" | "saved" | "failed"): void {
    const el = this.endNote;
    el.dataset["state"] = state;
    el.textContent =
      state === "saving"
        ? "Saving your route…"
        : state === "saved"
          ? "Route saved, to train the robot."
          : "Route not saved — no connection. Download JSON keeps a copy.";
  }

  /**
   * Show which view the NEXT press will give, not the one in use: a button
   * labelled with what you are already looking at tells you nothing.
   */
  setView(view: "third" | "first"): void {
    const next = view === "first" ? "third" : "first";
    this.btnView.innerHTML = icon(next === "first" ? "eye" : "video", 22);
    this.btnView.setAttribute(
      "aria-label",
      next === "first" ? "Switch to the driver's view" : "Switch to the chase view",
    );
  }

  /**
   * Show the recording control. Hidden on a browser that cannot record: an
   * offer that fails on the press is worse than no offer.
   */
  setRecordAvailable(available: boolean): void {
    this.btnRecord.classList.toggle("hidden", !available);
  }

  /** The base name the player typed, cleaned up by the caller. */
  recordBaseName(): string {
    return this.recordName.value;
  }

  /** Prefill the name (a saved choice, or ?record= from the URL). */
  setRecordName(name: string): void {
    this.recordName.value = name;
  }

  /** Arm or disarm from outside (the URL parameter, or a failure to start). */
  setRecordArmedState(armed: boolean): void {
    this.recordArmed = armed;
    this.renderRecord();
  }

  /** Say what happened to the last take, under the name field. */
  setRecordNote(text: string): void {
    this.recordNote.textContent = text;
  }

  private setRecordArmed(armed: boolean, cb: HudCallbacks): void {
    this.recordArmed = armed;
    this.renderRecord();
    cb.onRecordArm(armed, this.recordName.value);
  }

  private renderRecord(): void {
    this.btnRecord.classList.toggle("recording", this.recordArmed);
    this.btnRecord.setAttribute(
      "aria-label",
      this.recordArmed ? "Stop recording the driver's view" : "Record the driver's view",
    );
    this.btnRecordArm.textContent = this.recordArmed ? "Stop recording" : "Start recording";
    if (this.recordNote.textContent === "") {
      this.recordNote.textContent =
        "Records what you see while you drive. The video and the run's JSON are saved under the same name, so they can be paired up later.";
    }
  }

  /** Whether a next map exists, so the end panel can offer it. */
  setMapSequence(has: boolean): void {
    this.mapSequence = has;
  }

  /** Hide the map picker (used when an attempt restarts under it). */
  hideMaps(): void {
    this.mapPanel.classList.add("hidden");
  }

  /**
   * A collision or kerb climb, the moment it happens.
   *
   * Deliberately loud and deliberately transient: the player needs to know
   * they hit something WHILE they are driving, and the durable record is the
   * end panel, not this.
   */
  showWarning(text: string, t: number): void {
    if (t <= this.lastWarningT) return;
    this.lastWarningT = t;
    this.warningEl.textContent = text;
    this.warningEl.classList.remove("hidden");
    // Restart the animation: two hits in quick succession must both flash.
    this.warningEl.style.animation = "none";
    void this.warningEl.offsetWidth;
    this.warningEl.style.animation = "";
    if (this.warningTimer !== null) window.clearTimeout(this.warningTimer);
    this.warningTimer = window.setTimeout(() => {
      this.warningEl.classList.add("hidden");
    }, 1800);
  }

  private hideWarning(): void {
    if (this.warningTimer !== null) {
      window.clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    this.warningEl.classList.add("hidden");
  }

  showEnd(
    outcome: Outcome,
    hasNext: boolean,
    report: RunReport | null = null,
    /** Mission runs get the delivery headline; the lab levels keep their own
     * outcome wording ("You passed on the left."), which is the research
     * result and not a substitute for it. */
    isMission = false,
  ): void {
    // A feedback word from the last encounter would otherwise be drawn across
    // the report (both sit around 30% of the viewport height).
    if (this.floatingTimer !== null) {
      window.clearTimeout(this.floatingTimer);
      this.floatingTimer = null;
    }
    this.floating.classList.add("hidden");
    this.hideWarning();
    this.panelText.textContent =
      isMission && outcome.completed ? "Delivery complete" : OUTCOME_TEXT[outcome.result];
    if (report) {
      this.panelReport.classList.remove("hidden");
      this.panelReport.innerHTML = renderReport(report);
    } else {
      this.panelReport.classList.add("hidden");
    }
    this.btnNext.style.display = hasNext && outcome.completed ? "" : "none";
    // The map sequence is the mission's progression; the lab levels have their
    // own "Next" (the level ladder) and must not grow a second one.
    // Hidden under the same rule as the other map controls: a button nobody
    // pressed has no business in a scripted recording.
    this.btnNextMap.style.display = this.mapSequence && isMission && this.mapControls ? "" : "none";
    this.panel.classList.remove("hidden");
  }

  hideEnd(): void {
    this.panel.classList.add("hidden");
  }
}

/**
 * The end-of-run report.
 *
 * Every collision and kerb climb is listed with the time it happened, how far
 * into the run, and how fast the robot was going — enough to scrub back to it
 * in the recording and check. The ratios below name their denominator, so
 * "94.2% of 213 m" can be recounted rather than believed.
 */
function renderReport(report: RunReport): string {
  const m = report.metrics;
  const c = m.comfort;

  // Two lines per row rather than five columns: a 390 px portrait screen
  // wraps "8.1 m/s" onto its own line inside a table cell, and the result is
  // unreadable at exactly the moment the player wants to know what they hit.
  const rows = report.events.length
    ? report.events
        .map((e) => {
          const repeat = e.count > 1 ? ` <span class="report-x">x${e.count}</span>` : "";
          const span =
            e.t_end > e.t ? `${e.t.toFixed(1)}-${e.t_end.toFixed(1)}s` : `${e.t.toFixed(1)}s`;
          return `<li>
            <div class="report-line">
              <span class="report-kind report-${e.kind}">${e.kind === "collision" ? "HIT" : "KERB"}</span>
              <span class="report-what">${escapeHtml(e.what)}</span>${repeat}
            </div>
            <div class="report-where">${span} &middot; ${e.travelled_m.toFixed(0)} m in &middot; ${e.speed_mps.toFixed(1)} m/s</div>
          </li>`;
        })
        .join("")
    : `<li class="report-clean">No collisions, no kerb climbs.</li>`;

  const metric = (label: string, value: string, note = "") =>
    `<tr><td>${label}${note ? `<span class="report-note">${note}</span>` : ""}</td><td>${value}</td></tr>`;

  return `
    <div class="report-section">What happened</div>
    <ul class="report-events">${rows}</ul>

    <div class="report-section">Path</div>
    <table class="report-metrics">
      ${metric("Distance driven", `${m.distance_m.toFixed(0)} m`)}
      ${metric("In the drivable area", `${m.drivable_compliance_pct.toFixed(1)}%`, "of distance")}
      ${metric("Hugging the kerb", `${m.kerb_hug_pct.toFixed(1)}%`, `within ${KERB_HUG_M} m`)}
      ${metric(
        "Kept in to the kerb",
        `${m.nearside_discipline_pct.toFixed(1)}%`,
        m.adrift_m > 1 ? `${m.adrift_m.toFixed(0)} m out in a clear road` : "of clear road",
      )}
      ${metric("Wrong side of the road", `${m.wrong_side_m.toFixed(0)} m`, m.wrong_side_m > 1 ? "left of the centre line" : "")}
      ${metric("Driven over the kerb", `${m.off_road_m.toFixed(1)} m`)}
    </table>

    <div class="report-section">Safety</div>
    <table class="report-metrics">
      ${metric(
        "Collisions",
        `${m.collisions} (${m.collisions_per_100m.toFixed(2)} per 100 m)`,
        m.collision_contacts > m.collisions ? `${m.collision_contacts} contacts` : "",
      )}
      ${metric("Kerb climbs", `${m.kerb_climbs} (${m.kerb_climbs_per_100m.toFixed(2)} per 100 m)`)}
      ${metric("Driven over", `${m.rough_rides}`, "debris, speed bumps")}
      ${metric("Fell into a hole", `${m.fall_ins}`, m.fall_ins > 0 ? "the run ended there" : "")}
      ${metric("Closest time to collision", m.min_ttc_s === null ? "never closing" : `${m.min_ttc_s.toFixed(2)} s`)}
      ${metric("Near misses", `${m.ttc_conflicts}`, `TTC &lt; ${TTC_CONFLICT_S} s`)}
      ${metric("Time in a near miss", `${m.ttc_conflict_pct.toFixed(1)}%`, "of moving time")}
    </table>

    <div class="report-section">Comfort</div>
    <table class="report-metrics">
      ${metric("Braking / accelerating", `${c.accel_long_rms.toFixed(2)} m/s²`, "RMS")}
      ${metric("Cornering", `${c.accel_lat_rms.toFixed(2)} m/s²`, "RMS")}
      ${metric("Harshest jolt", `${c.accel_long_peak.toFixed(2)} m/s²`, "peak")}
      ${metric("Lateral jerk", `${c.jerk_lat_rms.toFixed(2)} m/s³`, "RMS")}
      ${metric("Harsh jerk", `${c.harsh_jerk_pct.toFixed(1)}%`, `over ${COMFORT_JERK_LAT_MPS3} m/s³`)}
      ${metric("ISO 2631-1 band", escapeHtml(c.iso_band), `${c.iso_combined_rms.toFixed(2)} m/s² combined`)}
    </table>
    <div class="report-caveat">
      ISO 2631-1 rates whole-body vibration; this simulation has no suspension
      and no vertical channel, so the band compares runs with each other rather
      than certifying a ride. The jerk threshold is the 50% comfort limit from
      de Winkel et al. (2023).
    </div>`;
}

function escapeHtml(v: string): string {
  return v.replace(/[&<>"]/g, (ch) =>
    ch === "&" ? "&amp;" : ch === "<" ? "&lt;" : ch === ">" ? "&gt;" : "&quot;",
  );
}
